import moment from 'moment';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { Row } from 'reactstrap';
import ActiveClients from '../../../components/LiveDashboard/v2/ActiveClients';
import Announcements from '../../../components/LiveDashboard/v2/Announcements';
import AssociateStatusCards from '../../../components/LiveDashboard/v2/AssociateStatusCards';
import ClientDetails from '../../../components/LiveDashboard/v2/ClientDetails';
import DepartmentPieChart from '../../../components/LiveDashboard/v2/DepartmentPieChart';
import LiveTracking from '../../../components/LiveDashboard/v2/LiveTracking';
import MonthlyAttendanceChart from '../../../components/LiveDashboard/v2/MonthlyAttendanceChart';
import RecentActivities from '../../../components/LiveDashboard/v2/RecentActivities';
import RecentFeedback from '../../../components/LiveDashboard/v2/RecentFeedback';
import { GET, POST, newFetch } from '../../../helpers/fetch';
import AppContext from '../../../store/App/contexts';

import './styles.css';

const LiveDashboard = () => {
  const { selectedClientID, selectedSiteID } = React.useContext(AppContext);
  const type = localStorage.getItem('type');
  const clientID = parseInt(selectedClientID, 10);
  const siteID = parseInt(selectedSiteID, 10);
  const access = JSON.parse(localStorage.getItem('access'));
  const siteIDs = React.useMemo(() => {
    if (siteID > 0) return [siteID];

    try {
      const clientOptions = JSON.parse(
        localStorage.getItem('clientSiteOptions') || '[]'
      );
      const selectedClient = clientOptions.find(
        (client) => client.id === clientID
      );

      return (selectedClient?.sites || [])
        .filter((site) => site.active === true)
        .map((site) => site.id);
    } catch (error) {
      return [];
    }
  }, [clientID, siteID]);
  const siteIDsParam = siteIDs.join(',');
  const siteIDsQuery = siteIDsParam ? `?site_ids=${siteIDsParam}` : '';
  const [activeDate, _] = React.useState(moment());
  const [clients, setClients] = React.useState([]);
  const [activeShift, setActiveShift] = React.useState(null);
  const [schedules, setSchedules] = React.useState(null);
  const [activeSchedules, setActiveSchedules] = React.useState([]);
  const [currentYear, setCurrentYear] = React.useState(
    new Date().getFullYear()
  );
  const [monthlyAttendance, setMonthlyAttendance] = React.useState({});
  const [feedback, setFeedback] = React.useState({});
  const [associateCount, setAssociateCount] = React.useState({
    activeAssociates: 0,
    inactiveAssociates: 0,
    associatesTimeInToday: 0,
    assignedAssociates: 0,
  });
  const [clientSettings, setClientSettings] = React.useState([]);
  const [associateCountDepartment, setAssociateCountDepartment] =
    React.useState([]);
  const [announcements, setAnnouncements] = React.useState([]);
  const [attendanceLogs, setAttendanceLogs] = React.useState([]);
  const [associateLocation, setAssociateLocation] = React.useState([]);
  const [clientDetails, setClientDetails] = React.useState([]);
  const [shifts, setShifts] = React.useState([]);
  const [fillRates, setFillRates] = React.useState(null);
  const [loadingCountDept, setLoadingCountDept] = React.useState(true);
  const [clientsSettings, setClientsSettings] = React.useState([]);

  React.useEffect(() => {
    if (!isNaN(clientID) && siteIDs.length > 0) {
      fetchAssociateCount();
      fetchAttendanceLog();
      fetchAssociateLocation();
      fetchMonthlyAttendance();
      fetchFeedback();
      fetchClientDetails();
      fetchAnnouncement();
      fetchAssociateCountDepartment();
      fetchClients();
      fetchSiteSettings();
    }
  }, [currentYear, clientID, siteID, siteIDsParam]);

  React.useEffect(() => {
    fetchClientsSettings();
  }, [clientID]);

  React.useEffect(() => {
    if (activeDate && siteIDs.length > 0) {
      fetchSchedules(siteIDs);
      fetchShifts(siteIDs);
    }
  }, [activeDate, siteID, siteIDsParam]);

  React.useEffect(() => {
    if (schedules !== null && schedules.length) {
      let active_schedule = schedules.filter(
        (schedule) => schedule.date === moment().format('YYYY-MM-DD')
      );
      setActiveSchedules(active_schedule);
    } else {
      setActiveSchedules([]);
    }
  }, [schedules]);

  React.useEffect(() => {
    const setFilLRatesArr = async (attendanceArr) => {
      let totalFilled = 0;
      let totalSlots = 0;

      for (let schedule of activeSchedules) {
        let scheduleAttendance = attendanceArr.find(
          (scheduleAttendance) => scheduleAttendance.schedule_id === schedule.id
        ) || { assigned: [], late: [], absent: [] };
        let isFixed = [
          ...scheduleAttendance.assigned,
          ...scheduleAttendance.late,
          ...scheduleAttendance.absent,
        ].filter((associate) => associate.is_fixed).length;

        let actualFilledSlots = schedule.filled_slots + isFixed;
        let actualTotalSlots = schedule.slots.slots + isFixed;

        totalFilled += actualFilledSlots;
        totalSlots += actualTotalSlots;
      }

      let fillRatesArr = [];

      for (let shift of shifts) {
        let schedule = activeSchedules.find(
          (schedule) => schedule.shift_id === shift.id
        );

        if (schedule) {
          let attendance = attendanceArr.find(
            (scheduleAttendance) =>
              scheduleAttendance.schedule_id === schedule.id
          );

          let isFixedCount = attendance
            ? [
                ...attendance.assigned,
                ...attendance.late,
                ...attendance.absent,
              ].filter((associate) => associate.is_fixed).length
            : 0;

          let overwriteSchedule = {
            ...shift,
            schedule: schedule
              ? {
                  ...schedule,
                  filled_slots: schedule.filled_slots + isFixedCount,
                  slots: {
                    ...schedule.slots,
                    slots: schedule.slots.slots + isFixedCount,
                  },
                }
              : null,
          };

          fillRatesArr.push(overwriteSchedule);
        } else {
          fillRatesArr.push({
            ...shift,
            schedule: null,
          });
        }
      }

      setFillRates(fillRatesArr);
    };

    const runAsyncFunctions = async () => {
      if (activeSchedules.length > 0) {
        const attendanceArrResponse = await Promise.all(
          activeSchedules.map(
            async (schedule) =>
              await newFetch(GET, `/schedule/${schedule.id}/associates`)
          )
        );

        const attendanceArr = attendanceArrResponse
          .filter((res) => res.success)
          .map((res) => res.data);

        await setFilLRatesArr(attendanceArr);
      } else {
        setFillRates(shifts.map((shift) => ({ ...shift, schedule: null })));
      }
    };

    if (activeSchedules.length > 0) {
      let timer = setTimeout(() => runAsyncFunctions(), 200);
      return () => {
        clearTimeout(timer);
      };
    } else {
      setFillRates(null);
    }
  }, [activeSchedules, shifts]);

  const fetchClients = async () => {
    let api_path = '/client/associatecount';
    let response = await newFetch(GET, api_path);
    if (response.success) {
      let filtered = response.data.filter((client) =>
        type === 'superadmin' ? client.active === true : client.id === clientID
      );
      setClients(filtered);
    }
  };

  const fetchSchedules = async (siteIds) => {
    const responses = await Promise.all(
      siteIds.map(async (siteId) => ({
        siteId,
        response: await newFetch(POST, `/site/${siteId}/schedule`, {
          start_date: moment(activeDate).format('YYYY-MM-DD'),
          end_date: moment(activeDate).format('YYYY-MM-DD'),
        }),
      }))
    );
    const scheduleData = responses.reduce(
      (data, { siteId, response }) =>
        response.success
          ? [
              ...data,
              ...response.data.map((schedule) => ({
                ...schedule,
                site_id: siteId,
              })),
            ]
          : data,
      []
    );

    setSchedules(scheduleData.length > 0 ? scheduleData : null);
  };

  const fetchShifts = async (siteIds) => {
    const responses = await Promise.all(
      siteIds.map(async (siteId) => ({
        siteId,
        response: await newFetch(GET, `/site/${siteId}/shifts`),
      }))
    );
    const shiftsData = responses.reduce(
      (data, { siteId, response }) =>
        response.success
          ? [
              ...data,
              ...response.data.map((shift) => ({ ...shift, site_id: siteId })),
            ]
          : data,
      []
    );

    if (shiftsData.length > 0) {
      setShifts(shiftsData);
      setActiveShift(shiftsData[0].id);
    } else {
      setShifts([]);
      setActiveShift(null);
    }
  };

  const fetchSiteSettings = async () => {
    const api_path = `/dashboard/site_settings/site/${siteID}${siteIDsQuery}`;
    const response = await newFetch(GET, api_path);
    if (response.success) {
      setClientSettings(response.data);
    } else {
      setClientSettings([]);
    }
  };

  const fetchAssociateCount = async () => {
    const api_path = `/dashboard/associate_count/client/${clientID}/site/${siteID}${siteIDsQuery}`;
    const response = await newFetch(GET, api_path);
    if (response.success) {
      setAssociateCount(response.data);
    }
  };

  const fetchAssociateCountDepartment = async () => {
    setLoadingCountDept(true);
    const api_path = `/dashboard/associate_count_department/site/${siteID}${siteIDsQuery}`;
    const response = await newFetch(GET, api_path);
    if (response.success) {
      setAssociateCountDepartment(
        response.data.map((item) => ({
          department: item.department,
          associatesCount: item.activeAssociates,
        }))
      );
    } else {
      setAssociateCountDepartment([]);
    }
    setLoadingCountDept(false);
  };

  const fetchAnnouncement = async () => {
    const api_path = `/dashboard/announcements`;
    const response = await newFetch(POST, api_path, {
      site_id: siteID,
      site_ids: siteIDs,
    });
    if (response.success) {
      setAnnouncements(response.data);
    }
  };

  const fetchAttendanceLog = async () => {
    const api_path = `/dashboard/associate_attendance_log/site/${siteID}${siteIDsQuery}`;
    const response = await newFetch(GET, api_path);
    if (response.success) {
      setAttendanceLogs(response.data);
    }
  };

  const fetchMonthlyAttendance = async () => {
    const api_path = `/dashboard/monthlyattendance/client/${clientID}/site/${siteID}/year/${currentYear}${siteIDsQuery}`;
    try {
      const response = await newFetch(GET, api_path);
      if (response.success) {
        setMonthlyAttendance(response.data);
      } else {
        setMonthlyAttendance({ monthly: [], yearly: {} });
      }
    } catch (error) {
      setMonthlyAttendance({ monthly: [], yearly: {} });
    }
  };

  const fetchFeedback = async () => {
    const api_path = `/dashboard/feedback/client/${clientID}/site/${siteID}${siteIDsQuery}`;
    try {
      const response = await newFetch(GET, api_path);
      if (response.success) {
        setFeedback(response.data);
      } else {
        setFeedback({});
      }
    } catch (error) {
      setFeedback({});
    }
  };

  const fetchAssociateLocation = async () => {
    const api_path = `/dashboard/associate_location/site/${siteID}${siteIDsQuery}`;
    const response = await newFetch(GET, api_path);
    if (response.success) {
      setAssociateLocation(response.data);
    } else {
      setAssociateLocation([]);
    }
  };

  const clientDetailsHandler = (data) => {
    const groupedData = Object.values(
      data.reduce((acc, item) => {
        if (!acc[item.client_id]) {
          acc[item.client_id] = {
            client_id: item.client_id,
            client_name: item.client_name,
            sites: {}, // Store sites under each client
          };
        }

        if (!acc[item.client_id].sites[item.site_id]) {
          acc[item.client_id].sites[item.site_id] = {
            site_id: item.site_id,
            site_name: item.site_name,
            assigned: 0,
            present: 0,
            absent: 0,
            associates: [],
          };
        }

        // Add associate details
        acc[item.client_id].sites[item.site_id].associates.push({
          associate_id: item.associate_id,
          first_name: item.first_name,
          last_name: item.last_name,
          schedule_id: item.schedule_id,
          status: item.status,
          status_id: item.status_id,
          date: item.date,
        });

        // Increment assigned count
        acc[item.client_id].sites[item.site_id].assigned++;

        // Count present associates
        if (item.status === 'present') {
          acc[item.client_id].sites[item.site_id].present++;
        }

        // Count absent associates
        if (item.status === 'absent') {
          acc[item.client_id].sites[item.site_id].absent++;
        }

        return acc;
      }, {})
    ).map((client) => ({
      ...client,
      sites: Object.values(client.sites), // Convert sites object to array
    }));

    return groupedData;
  };

  const fetchClientDetails = async () => {
    const api_path = `/dashboard/client_details/client/${clientID}/site/${siteID}${siteIDsQuery}`;
    const response = await newFetch(GET, api_path);
    if (response.success) {
      const newClientDetails = clientDetailsHandler(response.data);
      setClientDetails(newClientDetails);
    }
  };

  const fetchClientDetailsByScheduleId = async (schedule_id, site_id) => {
    const api_path = `/dashboard/client_details/client/${clientID}/site/${site_id}/schedule/${schedule_id}`;
    const response = await newFetch(GET, api_path);
    if (response.success) {
      const newClientDetails = clientSchedulesHandler(response.data);

      const newData = clientDetails.map((client) => ({
        ...client,
        sites: client.sites.map((site) => {
          if (site.site_id !== site_id) return site;

          const matchingSite = newClientDetails.find(
            (schedule) => schedule.site_id === site.site_id
          );

          return matchingSite
            ? { ...site, ...matchingSite }
            : {
                site_id: site.site_id,
                site_name: site.site_name,
                assigned: 0,
                present: 0,
                absent: 0,
                associates: [],
              };
        }),
      }));

      setClientDetails(newData);
    }
  };

  const clientSchedulesHandler = (data) => {
    const groupedData = Object.values(
      data.reduce((acc, item) => {
        if (!acc[item.site_id]) {
          acc[item.site_id] = {
            site_id: item.site_id,
            site_name: item.site_name,
            assigned: 0,
            present: 0,
            absent: 0,
            associates: [],
          };
        }

        // Add associate details
        acc[item.site_id].associates.push({
          associate_id: item.associate_id,
          first_name: item.first_name,
          last_name: item.last_name,
          schedule_id: item.schedule_id,
          status: item.status,
          status_id: item.status_id,
          date: item.date,
        });

        // Increment assigned count
        acc[item.site_id].assigned++;

        // Count present associates
        if (item.status === 'present') {
          acc[item.site_id].present++;
        }

        // Count absent associates
        if (item.status === 'absent') {
          acc[item.site_id].absent++;
        }

        return acc;
      }, {})
    );

    return groupedData;
  };

  const fetchClientsSettings = async () => {
    if (!isNaN(clientID)) {
      let api_path = `/client/${clientID}`;
      let response = await newFetch(GET, api_path);

      if (response.success) {
        setClientsSettings(response.data);
      }
    }
  };

  return (
    <div className="container-fluid dashboard-page">
      <div className="page-heading">
        <div>
          <span className="page-eyebrow">Workspace overview</span>
          <h1 className="module-title">Dashboard</h1>
          <p className="page-subtitle">Monitor workforce activity, attendance, and operational updates.</p>
        </div>
      </div>

      {clientsSettings.enable_smart_scheduler && (
        <AssociateStatusCards associateCount={associateCount} />
      )}

      <Row className="h-100 mb-4">
        <DepartmentPieChart
          associateCountDepartment={associateCountDepartment}
          loadingCountDept={loadingCountDept}
        />
        <Announcements announcements={announcements} />
      </Row>

      <Row className="h-100 mb-4">
        {clientsSettings.enable_smart_scheduler && (
          <MonthlyAttendanceChart
            monthlyAttendance={monthlyAttendance}
            currentYear={currentYear}
            setCurrentYear={setCurrentYear}
          />
        )}
        {clientsSettings.enable_communication && (
          <RecentFeedback feedback={feedback} />
        )}
        {!clientsSettings.enable_communication && (
          <RecentActivities attendanceLogs={attendanceLogs} />
        )}
      </Row>

      <Row className="h-100 mb-4">
        {(clientSettings.enable_geolocation === true ||
          clientSettings.enable_geofencing === true) && (
          <LiveTracking
            associateLocation={associateLocation}
            clientsSettings={clientsSettings}
          />
        )}
        {clientsSettings.enable_communication &&
          clientsSettings.enable_smart_scheduler && (
            <RecentActivities attendanceLogs={attendanceLogs} />
          )}
      </Row>

      <Row className="h-100 mb-4">
        <ActiveClients clients={clients} type={type} />
      </Row>

      <Row className="h-100 mb-4">
        {clientsSettings.enable_smart_scheduler && (
          <ClientDetails
            clientDetails={clientDetails}
            activeSchedules={activeSchedules}
            fillRates={fillRates}
            activeShift={activeShift}
            setActiveShift={setActiveShift}
            fetchClientDetailsByScheduleId={fetchClientDetailsByScheduleId}
          />
        )}
      </Row>
    </div>
  );
};

export default withRouter(LiveDashboard);
